import type { Submission_QuestionType as QuestionType } from '__generated__/graphql-types';
import type { Submission_SubmissionPart as SubmissionPart } from '__generated__/graphql-types-do-not-use';
import moment from 'moment';

import type {
  FeedbackPartFragment,
  RubricFeedbackFragment,
} from 'bundles/assignment-submission/components/api/assignment-submission-api/__generated__/AssignmentFeedbackFragments';
import type { SubmissionPartFragment } from 'bundles/assignment-submission/components/api/assignment-submission-api/__generated__/AssignmentSubmission';

import _t from 'i18n!nls/assignment-submission';

export type Part = SubmissionPartFragment | FeedbackPartFragment | RubricFeedbackFragment;

export type PartTypename =
  | Required<SubmissionPartFragment>['__typename']
  | Required<FeedbackPartFragment>['__typename'];

export type PartTypenameWithoutResponse = 'Submission_OffPlatformQuestion' | 'Submission_TextBlock';
export const partsWithoutResponse: PartTypenameWithoutResponse[] = [
  'Submission_OffPlatformQuestion',
  'Submission_TextBlock',
];

export type PartTypenameWithResponse = Exclude<PartTypename, PartTypenameWithoutResponse>;

export type ResponseFieldName =
  | 'checkboxResponse'
  | 'checkboxReflectResponse'
  | 'codeExpressionResponse'
  | 'fileUploadResponse'
  | 'mathResponse'
  | 'multipleChoiceResponse'
  | 'multipleChoiceReflectResponse'
  | 'multipleFillableBlanksResponse'
  | 'numericResponse'
  | 'offPlatformResponse'
  | 'plainTextResponse'
  | 'regexResponse'
  | 'richTextResponse'
  | 'textBlockResponse'
  | 'textExactMatchResponse'
  | 'textReflectResponse'
  | 'urlResponse'
  | 'widgetResponse';

export type SubmissionQuestionPartTypeWithResponse = Exclude<
  SubmissionPart['__typename'],
  undefined | PartTypenameWithoutResponse
>;

/* eslint-disable camelcase */
export const PartTypeToQuestionType: Record<SubmissionQuestionPartTypeWithResponse, QuestionType> = {
  Submission_FileUploadQuestion: 'FILE_UPLOAD',
  Submission_PlainTextQuestion: 'PLAIN_TEXT',
  Submission_RichTextQuestion: 'RICH_TEXT',
  Submission_UrlQuestion: 'URL',
  Submission_MultipleChoiceQuestion: 'MULTIPLE_CHOICE',
  Submission_MultipleChoiceReflectQuestion: 'MULTIPLE_CHOICE_REFLECT',
  Submission_CheckboxQuestion: 'CHECKBOX',
  Submission_CheckboxReflectQuestion: 'CHECKBOX_REFLECT',
  Submission_MultipleFillableBlanksQuestion: 'MULTIPLE_FILLABLE_BLANKS',
  Submission_NumericQuestion: 'NUMERIC',
  Submission_MathQuestion: 'MATH',
  Submission_TextExactMatchQuestion: 'TEXT_EXACT_MATCH',
  Submission_RegexQuestion: 'REGEX',
  Submission_TextReflectQuestion: 'TEXT_REFLECT',
  Submission_CodeExpressionQuestion: 'CODE_EXPRESSION',
  Submission_WidgetQuestion: 'WIDGET',
} as const;
/* eslint-enable camelcase */

export const getInvalidUrlError = (partNumber?: number) =>
  partNumber
    ? _t('The URL you entered for Question #{partNumber} is invalid', { partNumber })
    : _t('The URL you entered is invalid');

/** maximumAttemptDuration is the maximum milliseconds that a timed attempt can run for. It is 99 hours.
 * This is the upper limit set in authoring and is partly because JS setTimeout breaks with long durations
 * See https://github.com/webedx-spark/web/blob/b2eafd709a609973ba9fb0381e876c4071effb98/static/bundles/author-assignment/core/views/settings/DurationEditor.tsx#L82 */
export const maximumAttemptDuration = moment.duration(5940, 'minutes').asMilliseconds();

export const EVENTING_THROTTLE_TIME_MS_TYPING = 5000;

// default textInput event names for events related to learners interacting with text input based questions
export const EVENTING_TEXT_INPUT_NAME_TYPING = 'assignment_attempt_question_text_input_typing';
export const EVENTING_TEXT_INPUT_NAME_FOCUS = 'assignment_attempt_question_text_input_focus';
export const EVENTING_TEXT_INPUT_NAME_BLUR = 'assignment_attempt_question_text_input_blur';

// default button event names for events related to learners interacting with button based questions
export const EVENTING_BUTTON_NAME_CLICK_CODE_EXPRESSION_RUN_CODE =
  'assignment_attempt_question_code_expression_run_code';
export const EVENTING_BUTTON_NAME_CLICK_CODE_EXPRESSION_RESET = 'assignment_attempt_question_code_expression_reset';
export const EVENTING_BUTTON_NAME_CLICK_FILE_UPLOAD_ADD_FILE = 'assignment_attempt_question_file_upload_add_file';
export const EVENTING_BUTTON_NAME_CLICK_FILE_UPLOAD_REMOVE_FILE = 'assignment_attempt_question_file_upload_remove_file';
export const EVENTING_BUTTON_NAME_CLICK_FILE_UPLOAD_OPEN_FILE = 'assignment_attempt_question_file_upload_open_file';

// default checkbox event names for events related to learners interacting with checkbox/radio based questions
export const EVENTING_CHECKBOX_NAME_CLICK_MULTIPLE_CHOICE = 'assignment_attempt_question_multiple_choice_checkbox';
export const EVENTING_RADIO_NAME_CHANGE_MULTIPLE_CHOICE = 'assignment_attempt_question_multiple_choice_radio';

// default select event names for events related to learners interacting with select based questions
export const EVENTING_SELECT_NAME_CHANGE_FILLABLE_BLANK = 'assignment_attempt_question_fillable_blank_select';

// default script load event names for events related to learners interacting with widget based questions
export const EVENTING_SCRIPT_NAME_LOAD_WIDGET = 'assignment_attempt_question_widget_script_load';

// skill diagnostic button event names for events related to learners interacting with skill diagnostic pages

/** Tracks when a learner clicks to edit their skill diagnostic responses from the result page */
export const EVENTING_BUTTON_NAME_SKILL_DIAGNOSTIC_COVER_PAGE_EDIT_DIAGNOSTIC =
  'skill_diagnostic_cover_page_edit_diagnostic';

/** Tracks when a learner clicks to start a new skill diagnostic from the cover page */
export const EVENTING_BUTTON_NAME_SKILL_DIAGNOSTIC_COVER_PAGE_START_DIAGNOSTIC =
  'skill_diagnostic_cover_page_start_diagnostic';

// TODO (ishaguy): Update this to skill_diagnostic_cover_page_resume_diagnostic when new events are added
/** Tracks when a learner clicks to resume an in-progress skill diagnostic from the cover page */
export const EVENTING_BUTTON_NAME_SKILL_DIAGNOSTIC_COVER_PAGE_RESUME_DIAGNOSTIC =
  'skill_diagnostic_cover_page_edit_diagnostic';

/** Tracks when a learner clicks to cancel the skill diagnostic from the cover page */
export const EVENTING_BUTTON_NAME_SKILL_DIAGNOSTIC_COVER_PAGE_CANCEL_DIAGNOSTIC =
  'skill_diagnostic_cover_page_cancel_diagnostic';

/** Tracks when a learner chooses to keep their previous skill diagnostic responses */
export const EVENTING_BUTTON_NAME_SKILL_DIAGNOSTIC_COVER_PAGE_KEEP_PREVIOUS_RESPONSES =
  'skill_diagnostic_cover_page_keep_previous_responses';

/** Tracks when a learner submits their skill diagnostic responses from the question page */
export const EVENTING_BUTTON_NAME_SKILL_DIAGNOSTIC_QUESTION_PAGE_SUBMIT_DIAGNOSTIC =
  'skill_diagnostic_question_page_submit_diagnostic';

/** Tracks when a learner clicks to start focused learning path from the skill diagnostic result page */
export const EVENTING_BUTTON_NAME_SKILL_DIAGNOSTIC_RESULT_PAGE_START_FOCUSED_LEARNING =
  'skill_diagnostic_result_page_start_focused_learning';

/** Tracks when a learner clicks to start learning with default path from the skill diagnostic result page */
export const EVENTING_BUTTON_NAME_SKILL_DIAGNOSTIC_RESULT_PAGE_START_DEFAULT_LEARNING =
  'skill_diagnostic_result_page_start_default_learning';

/** Tracks when a learner clicks to review all content from the skill diagnostic result page */
export const EVENTING_BUTTON_NAME_SKILL_DIAGNOSTIC_RESULT_PAGE_REVIEW_ALL_CONTENT =
  'skill_diagnostic_result_page_review_all_content';

/** Tracks when a learner clicks to start skill assessment from the skill diagnostic result page */
export const EVENTING_BUTTON_NAME_SKILL_DIAGNOSTIC_RESULT_PAGE_START_SKILL_ASSESSMENT =
  'skill_diagnostic_result_page_start_skill_assessment';

/** Tracks when a learner clicks to restart the skill diagnostic from the result page */
export const EVENTING_BUTTON_NAME_SKILL_DIAGNOSTIC_RESULT_PAGE_RESTART_DIAGNOSTIC =
  'skill_diagnostic_result_page_restart_diagnostic';

export const SKILL_DIAGNOSTIC_FOCUSABLE_ELEMENTS = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
] as const;

/**
 * Animation timing constants for skill diagnostic transitions
 */
export const ANIMATION_DURATION = 1000;
export const ANIMATION_DELAY = 250;
export const HIDE_DELAY = 650; // Slightly longer than animation duration

/**
 * Page type identifiers for skill diagnostic views
 */
export const PAGE_VIEW_TYPES = {
  /** Skill diagnostic cover page  */
  COVER: 'skill_diagnostic_cover_page',
  /** Skill diagnostic attempt page */
  ATTEMPT: 'skill_diagnostic_attempt_page',
  /** Skill diagnostic result page */
  RESULT: 'skill_diagnostic_result_page',
} as const;

export type PageViewType = (typeof PAGE_VIEW_TYPES)[keyof typeof PAGE_VIEW_TYPES];

/**
 * Learner view type identifiers for skill diagnostic views
 */
export const LEARNER_RESULT_VIEW_TYPES = {
  /** Skill diagnostic cover page  */
  EXPERIENCED: 'skill_diagnostic_experienced_learner_result_page',
  /** Skill diagnostic attempt page */
  INEXPERIENCED: 'skill_diagnostic_inexperienced_learner_result_page',
  /** Skill diagnostic result page */
  REGULAR: 'skill_diagnostic_regular_learner_result_page',
} as const;

export type LearnerResultViewType = (typeof LEARNER_RESULT_VIEW_TYPES)[keyof typeof LEARNER_RESULT_VIEW_TYPES];

/**
 * TACO namespace for quiz deprecation configuration.
 * This namespace contains the list of courses excluded from quiz deprecation.
 * Format: {domain}/{version}/{feature-name}
 */
export const TACO_NAMESPACE_PREVENT_ADDING_NEW_QUIZZES_EXCLUDED_COURSES =
  'assignments/v1/prevent-adding-new-quizzes-excluded-courses';
